import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHero } from "../components/shared/Primitives";
import { Mail, Send, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const isCarerReviewContext = searchParams.get("context") === "carer-review";
  const [form, setForm] = useState({
    name: "",
    email: searchParams.get("email") || "",
    audience: "carer",
    subject: isCarerReviewContext ? "Carer account identity review" : "",
    message: isCarerReviewContext
      ? "I believe my carer account was flagged for review in error. Please could you take another look? "
      : "",
  });
  const [status, setStatus] = useState({ sending: false, ok: false, error: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ sending: true, ok: false, error: "" });
    try {
      await api.post("/api/contact", form);
      setStatus({ sending: false, ok: true, error: "" });
      setForm({ name: "", email: "", audience: "carer", subject: "", message: "" });
    } catch (err) {
      setStatus({ sending: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="page-contact">
      <PageHero eyebrow="Contact" title="Say hello." description="Questions, feedback, press enquiries — we read every message." />
      <section className="section">
        <div className="container-jcc grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-base p-6">
              <div className="flex items-center gap-2 text-teal font-semibold"><Mail className="w-5 h-5" /> hello@justcarecommunity.com</div>
              <p className="mt-2 text-sm text-ink-muted">General enquiries. We aim to reply within two working days.</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex gap-3 text-sm text-amber-900" data-testid="contact-emergency">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                <strong>Please do not use this form for urgent care, medical or safeguarding emergencies.</strong> Contact emergency services, your local authority safeguarding team, NHS 111 or the appropriate professional as relevant.
              </span>
            </div>
          </div>
          <form onSubmit={onSubmit} className="lg:col-span-3 card-base p-8 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-1.5">Your name</label>
                <input required value={form.name} onChange={setField('name')} data-testid="contact-name"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Email address</label>
                <input required type="email" value={form.email} onChange={setField('email')} data-testid="contact-email"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">I am a…</label>
              <select value={form.audience} onChange={setField('audience')} data-testid="contact-audience"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none bg-white">
                <option value="carer">Carer</option>
                <option value="family">Family</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Subject (optional)</label>
              <input value={form.subject} onChange={setField('subject')} data-testid="contact-subject"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Message</label>
              <textarea required rows={6} value={form.message} onChange={setField('message')} data-testid="contact-message"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none" />
            </div>
            <button disabled={status.sending} className="btn-teal w-full md:w-auto" data-testid="contact-submit">
              <Send className="w-4 h-4" />{status.sending ? "Sending…" : "Send message"}
            </button>
            {status.ok && <div className="text-sm text-green-700" data-testid="contact-success">Thanks — we've received your message and will reply as soon as we can.</div>}
            {status.error && <div className="text-sm text-red-600" data-testid="contact-error">{status.error}</div>}
          </form>
        </div>
      </section>
    </div>
  );
}
